import {
  Box,
  Card,
  withStyles,
  Icon,
  Tooltip,
  CardTabs,
  InterestAccount,
  InterestRate,
  InterestAccountTransactionHistoryStatus,
  InterestAccountDetailRequest,
  InterestAccountTransactionsRequest,
  InterestAccruedDetail,
  SupportedCurrencies,
} from 'vopay';
import { format, subWeeks } from 'date-fns';
import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useFetchInterestAccountTransactions from '@/hooks/useApi/useFetchInterestAccountTransactions';
import useFetchInterestAccount from '@/hooks/useApi/useFetchInterestAccounts';
import useFetchInterestAccountRates from '@/hooks/useApi/useFetchInterestAccountRates';
import useFetchInterestAccountDetail from '@/hooks/useApi/useFetchInterestAccountDetail';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import useFileDownload from '@/hooks/useFiledDownload';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import styles from './InterestAccountsPage.module.scss';
import InterestAccountTransactionSearchForm from './InterestAccountTransactionSearchForm';
import InterestAccountTransactionTable from './InterestAccountTransactionTable';
import InterestHistoryTable from './InterestHistoryTable';
import InterestHistorySearchForm from './InterestHistorySearchForm';
import InterestAccountModal from './InterestAccountModal/InterestAccountModal';
import InterestAccountFundTransferModal from './InterestAccountFundTransferModal/InterestAccountFundTransferModal';
import { usePermissions } from '@/providers/Permissions.provider';
import { Title, Button, UtilButton } from 'UI';

const classes = withStyles(styles);

interface Account {
  AccountName: string;
  InterestMaturityType: string;
  CurrentBalance: string;
  InterestAccrued: string;
}

type Term = 'short-term' | 'long-term';
type RateInfo = {
  AnnualInterestRate: string;
  Term: Term;
  RangeStart: string;
  RangeEnd: string;
};

type QueryParams = {
  type: string;
  status: InterestAccountTransactionHistoryStatus | null;
  searchTerm: string;
  startDate: string;
  endDate: string;
};

type TransactionParams = {
  startDate: string;
  endDate: string;
  status: InterestAccountTransactionHistoryStatus | 'All' | null;
  timeZone: string;
  type: string;
  hasInvalidEndDate: boolean;
  searchTerm: string;
  accountType: string;
};

type InterestAccountDetailParams = {
  startDate: string;
  endDate: string;
  accountType: string;
  hasInvalidEndDate: boolean;
  timeZone: string;
};

export default function InterestAccountsPage() {
  const { userSettings } = useUserSettingsProvider();
  const location = useLocation();
  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      type: params.get('type') || '',
      status: params.get('status') as InterestAccountTransactionHistoryStatus | null,
      searchTerm: params.get('searchTerm') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);
  const { downloadCsvFile } = useFileDownload();

  const type = useMemo(() => queryParams.type || '', [queryParams]);
  const status = useMemo(() => queryParams.status || null, [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);

  const timeDifference = format(subWeeks(new Date(), 1), 'yyyy-MM-dd');
  const currentDateTime = format(new Date(), 'yyyy-MM-dd');

  const [modifiedInterestRates, setModifiedInterestRates] = useState<InterestRate[]>([]);
  const { permissions } = usePermissions();

  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [[fetching], setDataFetchingInfo] = useDataFetchIndicator();

  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);

  const [transactionSearch, setTransactionSearch] = useState(false);
  const [interestAccruedDetails, setInterestAccruedDetails] = useState<InterestAccruedDetail[]>([]);

  const [hasLongTermAccount, setHasLongTermAccount] = useState(false);
  const [isFundTransfer, setIsFundTransfer] = useState(false);

  const [showTransactionFilter, setShowTransactionFilter] = useState(false);
  const [showInterestFilter, setShowInterestFilter] = useState(false);
  const [isFetchingTransactionData, setIsFetchingTransactionData] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [cardvalue, setCardvalue] = useState('transaction');
  const [shortTermAccount, setShortTermAccount] = useState({
    AccountName: '',
    CurrentBalance: '0.00',
    InterestAccrued: '0.00',
  });
  const [longTermAccount, setLongTermAccount] = useState({
    AccountName: '',
    CurrentBalance: '0.00',
    InterestAccrued: '0.00',
  });

  const [InterestAccountTransactions, getInterestAccountTransactions] = useFetchInterestAccountTransactions({
    InterestMaturityType: '',
    StartDate: '',
    EndDate: '',
    Timezone: '',
  });

  const { interestAccounts, fetchInterestAccounts } = useFetchInterestAccount();
  const { interestRates, fetchInterestRates } = useFetchInterestAccountRates();

  const [InterestAccountDetailResponse, getInterestAccountDetail] = useFetchInterestAccountDetail({
    InterestMaturityType: '',
    Month: '',
    Year: '',
  });
  const { CurrentBalance } = shortTermAccount;
  const [transferType, setTransferType] = useState<'Fund' | 'Withdraw'>('Fund');
  const currency: SupportedCurrencies = 'CAD';
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const [shortTermInterest, longTermInterest] = ['short-term', 'long-term'].map((term: string) =>
    getInterestRate(
      term as Term,
      parseFloat(term === 'short-term' ? shortTermAccount.CurrentBalance : longTermAccount.CurrentBalance),
      transformInterestRates(interestRates),
    ),
  );

  useEffect(() => {
    if (permissions.ViewInterestAccounts) {
      fetchInterestAccounts({});

      if (permissions.ViewInterestAccounts) {
        fetchInterestRates({});
      }

      const InterestAccountDetailparams: InterestAccountDetailRequest = {
        InterestMaturityType: '',
        Month: '',
        Year: '',
      };
      getInterestAccountDetail(InterestAccountDetailparams);
    }
  }, []);

  useEffect(() => {
    if (isFetchingTransactionData) {
      setTransactionSearch(true);
      const request: InterestAccountTransactionsRequest = {
        StartDate: timeDifference,
        EndDate: currentDateTime,
        Timezone: userSettings.timezone,
      };
      getInterestAccountTransactions(request);
    }
  }, [isFetchingTransactionData]);

  useEffect(() => {
    setShortTermAccount(getAccountByMaturityType(interestAccounts, 'short-term'));
    setLongTermAccount(getAccountByMaturityType(interestAccounts, 'long-term'));
  }, [interestAccounts]);

  useEffect(() => {
    setModifiedInterestRates(transformInterestRateValues(interestRates));
  }, [interestRates]);

  useEffect(() => {
    if (longTermAccount.AccountName !== '') {
      setHasLongTermAccount(true);
    }
  }, [longTermAccount]);

  const fetchTransactionList = ({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    accountType: selectedMaturityType,
    status: selectedStatus,
    timeZone: selectedTimeZone,
    type: selectedType,
    hasInvalidEndDate: isEndDateInvalid,
    searchTerm: searchQuery,
  }: TransactionParams) => {
    const transactionRequestPayload = {
      StartDate: selectedStartDate,
      EndDate: selectedEndDate,
      Timezone: selectedTimeZone,
      InterestMaturityType:
        selectedMaturityType === null || selectedMaturityType === 'All' ? undefined : selectedMaturityType,
      TransactionStatus: selectedStatus === null || selectedStatus === 'All' ? undefined : selectedStatus,
      TransactionType: selectedType === 'All' || selectedType === '' ? undefined : selectedType,
      TransactionID: searchQuery,
    };

    setIsFetchingTransactionData(true);
    setHasInvalidEndDate(isEndDateInvalid);
    if (!isEndDateInvalid) {
      setDataFetchingInfo(true, 'Loading', 'fullScreen');
      setTransactionSearch(true);
      getInterestAccountTransactions(transactionRequestPayload);
    } else {
      setShouldShowEndDateError(true);
    }
  };

  const fetchInterestAccountDetail = ({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    timeZone: selectedTimeZone,
    accountType: selectedMaturityType,
    hasInvalidEndDate: isEndDateInvalid,
  }: InterestAccountDetailParams) => {
    const transactionRequestPayload = {
      InterestMaturityType: selectedMaturityType === null || selectedMaturityType === 'All' ? '' : selectedMaturityType,
      StartDate: selectedStartDate,
      EndDate: selectedEndDate,
      Timezone: selectedTimeZone,
    };

    setHasInvalidEndDate(isEndDateInvalid);
    if (!isEndDateInvalid) {
      setDataFetchingInfo(true, 'Loading', 'fullScreen');
      setTransactionSearch(true);
      getInterestAccountDetail(transactionRequestPayload);
    } else {
      setShouldShowEndDateError(true);
    }
  };

  useEffect(() => {
    if (transactionSearch && InterestAccountTransactions.Success) {
      setTransactionSearch(false);
      setDataFetchingInfo(false);
    }
    if (
      transactionSearch &&
      InterestAccountTransactions.Success === false &&
      InterestAccountTransactions.ErrorMessage !== ''
    ) {
      setTransactionSearch(false);
      setDataFetchingInfo(false);
    }
  }, [InterestAccountTransactions]);

  // export table
  useEffect(() => {
    if (isExportingCSV) {
      const tableConfigs = {
        interest: {
          tableID: 'interest-account-table',
          filename: 'Interest History',
        },
        transaction: {
          tableID: 'interestAccountTransactionTable',
          filename: 'Account Transaction',
        },
      };

      const { tableID, filename }: { tableID: string; filename: string } =
        tableConfigs[cardvalue as keyof typeof tableConfigs];

      downloadCsvFile(tableID, filename);
    }

    setIsExportingCSV(false);
  }, [isExportingCSV]);

  useEffect(() => {
    if (InterestAccountDetailResponse.Success) {
      const combinedDetails = Object.values(InterestAccountDetailResponse.InterestAccruedDetails ?? {});

      const sortedCombinedDetails = combinedDetails
        .sort((a, b) => {
          const dateA = new Date((a as { DateAccrued: string }).DateAccrued);
          const dateB = new Date((b as { DateAccrued: string }).DateAccrued);
          return dateA.valueOf() - dateB.valueOf();
        })
        .map((value) => ({
          DateAccrued: (value as { DateAccrued: string }).DateAccrued,
          AmountAccrued: (value as { AmountAccrued: string }).AmountAccrued,
          Rate: (value as { Rate: string }).Rate,
          BalanceForCalculation: (value as { BalanceForCalculation: string }).BalanceForCalculation,
          InterestMaturityType: (value as { InterestMaturityType: string }).InterestMaturityType,
        }));

      setInterestAccruedDetails(sortedCombinedDetails);
      setDataFetchingInfo(false);
    }
  }, [InterestAccountDetailResponse]);

  return (
    <div className={styles.interestAccountcontainer}>
      <Box pb="lg" flx="col">
        <Box jc="sb" ai="center" pb="smd">
          <Title>Interest Accounts</Title>

          <Card
            style={{
              padding: '1em',
              borderRadius: 10,
            }}
          >
            <Box flx="col">
              <Box className={classes('date')}>Today&apos;s Date</Box>
              <Box>
                <b>{formattedDate}</b>
              </Box>
            </Box>
          </Card>
        </Box>
        <div className={classes('mainCardContainer')}>
          <Card
            style={{
              padding: '1.5em',
            }}
            className={classes('cardContainer')}
            title={
              <Box>
                <div>Short Term</div>

                <div className={classes('tierLevel', 'tierLevelShortTerm')}>
                  <div className={classes('circleContainer')}>
                    {Array.from({ length: shortTermInterest.tiers }, (_, index) => (
                      <div
                        key={index}
                        className={classes(
                          'circle',
                          index < shortTermInterest.tierLevel ? 'shortTermActive' : 'shortTerm',
                        )}
                      />
                    ))}
                  </div>

                  <div className={classes('shortTermInterest')}>{shortTermInterest.interestRate}%</div>
                </div>
              </Box>
            }
          >
            <Card
              className={classes('interestAccountCard')}
              style={{ padding: '1.5em', width: '100%', borderRadius: 20 }}
            >
              <Box jc="sb" ai="center" gap="sm">
                <Box flx="col">
                  <div className={classes('shortTermBalance')}>
                    <div style={{ fontWeight: 'bold', paddingBottom: '10px' }}>
                      {currency} {formatAmount(shortTermAccount.CurrentBalance)}
                    </div>
                  </div>
                  <Box className={classes('availableBalanceContainer')}>
                    Available Balance
                    <Box ps="sm">
                      <Tooltip
                        borderColor="rgba(45, 45, 45, 0.6)"
                        icon="info"
                        position="top"
                        bgColor="#fbfbfb"
                        information={`The available balance may differ from the "End of Day Eligible Balance." Interest is calculated based on the remaining available balance at the end of the day, which becomes the "End of Day Eligible Balance." See the 'Interest' tab below.`}
                        popoverClassName={classes('tooltip')}
                      />
                    </Box>
                  </Box>
                </Box>

                <Card
                  style={{
                    padding: '0',
                    borderRadius: 12,
                    borderWidth: 0.6,
                  }}
                >
                  <Box flx="col" jc="center" ai="center" className={classes('shortTermInterestGained')}>
                    <div style={{ fontWeight: 'bold', paddingBottom: '5px', color: '#2d2d2d' }}>
                      {currency} {formatAmount(shortTermAccount.InterestAccrued)}
                    </div>

                    <div style={{ color: 'rgba(45, 45, 45, 0.8)' }}>
                      <span>Interest Gained</span>
                    </div>
                  </Box>
                </Card>
              </Box>
            </Card>
          </Card>

          {fetching ? null : hasLongTermAccount ? (
            <Card
              title={
                <Box width="100%" ai="center">
                  <div>Long Term</div>

                  <div className={classes('tierLevel', 'tierLevelLongTerm')}>
                    <div className={classes('circleContainer')}>
                      {Array.from({ length: longTermInterest.tiers }, (_, index) => (
                        <div
                          key={index}
                          className={classes(
                            'circle',
                            index < longTermInterest.tierLevel ? 'longTermActive' : 'longTerm',
                          )}
                        />
                      ))}
                    </div>

                    <div className={classes('longTermInterest')}>{longTermInterest.interestRate}%</div>

                    <Icon color="low-light" icon="chevron" size="xsm" />
                  </div>

                  {!!permissions.ManageInterestAccounts && (
                    <UtilButton
                      className={classes('addFundButton')}
                      id="add-more-funds-button"
                      disabled={!permissions.ManageInterestAccounts}
                      onClick={() => {
                        setIsFundTransfer(true);
                        setTransferType('Fund');
                      }}
                    >
                      {/* _TODO: i18n */}+ Add Funds
                    </UtilButton>
                  )}
                </Box>
              }
              style={{
                padding: '1.5em',
              }}
              className={classes('cardContainer')}
            >
              <Card className={classes('interestAccountCard')}>
                <Box jc="sb" ai="center">
                  <Box flx="col">
                    <div className={classes('shortTermBalance')}>
                      <div style={{ fontWeight: 'bold', paddingBottom: '10px' }}>
                        {currency} {formatAmount(longTermAccount.CurrentBalance)}
                      </div>
                    </div>
                    <Box className={classes('availableBalanceContainer')}>
                      Available Balance
                      <Box ps="sm" pe="sm">
                        <Tooltip
                          borderColor="rgba(45, 45, 45, 0.6)"
                          icon="info"
                          position="top"
                          bgColor="#fbfbfb"
                          information="Available balance is different from 'End of Day Eligible Balance'. Interest rates are applied on the Remaining Available Balance at the end of the day, which becomes the Eligible End of Day Balance."
                          popoverClassName={classes('tooltip')}
                        />
                      </Box>
                      {!!permissions.ManageInterestAccounts && (
                        <UtilButton
                          id="withdraw-button"
                          size="small"
                          disabled={!permissions.ManageInterestAccounts}
                          onClick={() => {
                            setIsFundTransfer(true);
                            setTransferType('Withdraw');
                          }}
                        >
                          {/* _TODO: i18n */}
                          Withdraw
                        </UtilButton>
                      )}
                    </Box>
                  </Box>
                  <Card
                    style={{
                      padding: '0',
                      borderRadius: 12,
                      borderWidth: 0.6,
                    }}
                  >
                    <Box flx="col" jc="center" ai="center" className={classes('shortTermInterestGained')}>
                      <div style={{ fontWeight: 'bold', paddingBottom: '5px' }}>
                        {currency} {formatAmount(longTermAccount.InterestAccrued)}
                      </div>
                      <div style={{ color: 'rgba(45, 45, 45, 0.8)' }}>
                        <span>Interest Gained</span>
                      </div>
                    </Box>
                  </Card>
                </Box>
              </Card>
            </Card>
          ) : (
            <Card
              style={{
                padding: '2em',
              }}
              className={classes('cardContainer')}
            >
              <Box>
                <Box ps="md" ai="center">
                  <Icon height="83px" width="83px" color="greyLight" icon="interestBank" size="xxlg" />
                </Box>

                <Box flx="col" jc="center">
                  <h2 className={classes('betterInterestTitle')}>Want better Interest Rates?</h2>

                  <div style={{ marginBottom: '24px' }}>Create a Long Term Interest Account and get even more!</div>

                  <Button
                    disabled={!permissions.ManageInterestAccounts}
                    colorScheme="primary"
                    onClick={() => {
                      setIsPreviewing(true);
                    }}
                  >
                    {/* _TODO: i18n */}
                    Get Started
                  </Button>
                </Box>
              </Box>
            </Card>
          )}
        </div>
      </Box>
      <Box pt="md" flx="col" position="relative">
        <CardTabs.Root defaultValue="transaction">
          <CardTabs.List aria-label="Interest Account">
            <CardTabs.Trigger
              value="transaction"
              onClick={() => {
                setCardvalue('transaction');
              }}
            >
              <span>Transactions</span>
            </CardTabs.Trigger>
            <CardTabs.Trigger
              value="interest"
              onClick={() => {
                setCardvalue('interest');
              }}
            >
              <span>Interest</span>
            </CardTabs.Trigger>
          </CardTabs.List>

          <CardTabs.Content value="transaction">
            <Box flx="col" className={classes('transactionTableContainer')} gap="common">
              <Box flx="col" gap="sm">
                <div className={classes('transactionTableFilterContainer')}>
                  <Icon
                    icon={showTransactionFilter ? 'filter2on' : 'filter2'}
                    width="14px"
                    height="14px"
                    color="none"
                  />

                  <button
                    type="button"
                    onClick={() => {
                      setShowTransactionFilter(!showTransactionFilter);
                    }}
                    className={classes('filterButton')}
                  >
                    {showTransactionFilter ? 'Hide Filters' : 'Show Filters'}
                  </button>
                </div>

                {showTransactionFilter && (
                  <InterestAccountTransactionSearchForm
                    type={type}
                    status={status}
                    searchTerm={searchTerm}
                    hasLongTermAccount={hasLongTermAccount}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    onSearch={(searchParams) => {
                      fetchTransactionList({
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd') || '',
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd') || '',
                        accountType: searchParams.accountType,
                        status: searchParams.status,
                        timeZone: searchParams.timeZone,
                        type: searchParams.type,
                        hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                        searchTerm: searchParams.searchTerm,
                      });
                    }}
                  />
                )}
              </Box>

              <InterestAccountTransactionTable
                InterestAccountTransactions={InterestAccountTransactions?.InterestAccountTransactions}
                currency={currency}
                formatDate={formatDate}
                getStatusIcon={getStatusIcon}
                onInit={() => setIsFetchingTransactionData(true)}
              />
            </Box>
          </CardTabs.Content>

          <CardTabs.Content value="interest">
            <Box flx="col" className={classes('transactionTableContainer')} gap="common">
              <Box flx="col" gap="sm">
                <div className={classes('transactionTableFilterContainer')}>
                  <Icon icon={showInterestFilter ? 'filter2on' : 'filter2'} width="14px" height="14px" color="none" />

                  <button
                    type="button"
                    id="filterButton"
                    onClick={() => {
                      setShowInterestFilter(!showInterestFilter);
                    }}
                    className={classes('filterButton')}
                  >
                    {showInterestFilter ? 'Hide Filters' : 'Show Filters'}
                  </button>
                </div>

                {showInterestFilter && (
                  <InterestHistorySearchForm
                    hasLongTermAccount={hasLongTermAccount}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    onSearch={(searchParams) => {
                      fetchInterestAccountDetail({
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                        timeZone: searchParams.timeZone,
                        accountType: searchParams.accountType,
                        hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                      });
                    }}
                  />
                )}
              </Box>

              <InterestHistoryTable
                interestAccruedDetails={interestAccruedDetails}
                formatDate={formatDate}
                hasLongTermAccount={hasLongTermAccount}
                currency={currency}
              />
            </Box>
          </CardTabs.Content>
        </CardTabs.Root>

        <Button
          className={classes('exportButton')}
          id="export-csv-button"
          onClick={() => {
            setIsExportingCSV(true);
          }}
        >
          {/* _TODO: i18n */}
          Export
        </Button>
      </Box>

      <InterestAccountModal
        InterestRates={modifiedInterestRates}
        visible={isPreviewing}
        currency={currency}
        modifiedInterestRates={modifiedInterestRates}
        currentBalance={CurrentBalance}
        formatAmount={formatAmount}
        onClose={(added: boolean) => {
          setIsPreviewing(false);
          if (added) fetchInterestAccounts({});
        }}
      />

      <InterestAccountFundTransferModal
        transferType={transferType}
        visible={isFundTransfer}
        currentBalance={transferType === 'Fund' ? CurrentBalance : longTermAccount.CurrentBalance}
        onClose={(added) => {
          setIsFundTransfer(false);
          if (added) fetchInterestAccounts({});
        }}
        currency={currency}
        formatAmount={formatAmount}
      />
    </div>
  );
  function formatDate(dateString: string): string {
    const hasTime = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(dateString);
    const date = hasTime ? new Date(dateString) : new Date(`${dateString}T00:00:00-08:00`);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day} ${year}`;
  }
  function formatAmount(number: string) {
    if (Number.isNaN(parseFloat(number)) || number === '' || number === undefined) return '0.00';

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(number));
  }
  function getAccountByMaturityType(accounts: InterestAccount[], maturityType: string): Account {
    const defaultAccount: Account = {
      AccountName: '',
      InterestMaturityType: '',
      CurrentBalance: '0.00',
      InterestAccrued: '0.00',
    };

    return Object.values(accounts).find((account) => account.InterestMaturityType === maturityType) || defaultAccount;
  }
  function getInterestRate(
    term: Term,
    balance: number,
    rates: RateInfo[],
  ): { interestRate: string; tierLevel: number; tiers: number } {
    const applicableRates = rates.filter((rate) => rate.Term === term);

    if (applicableRates.length === 0) {
      // If no applicable rates are found, return 0
      return {
        interestRate: '0.00',
        tierLevel: 0,
        tiers: 4,
      };
    }

    const matchingRate = applicableRates.find(
      (rate) => balance >= parseFloat(rate.RangeStart) && balance <= parseFloat(rate.RangeEnd),
    );

    if (!matchingRate) {
      // Check if balance exceeds the last tier's range
      const lastRate = applicableRates[applicableRates.length - 1];
      if (balance > parseFloat(lastRate.RangeEnd)) {
        return {
          interestRate: lastRate?.AnnualInterestRate ?? '0.00',
          tierLevel: applicableRates.length,
          tiers: applicableRates.length,
        };
      }
      // If balance is below the first tier's range, return 0
      return {
        interestRate: '0.00',
        tierLevel: 0,
        tiers: applicableRates.length,
      };
    }

    const tierLevel = applicableRates.indexOf(matchingRate);

    return {
      interestRate: matchingRate?.AnnualInterestRate ?? '0.00',
      tierLevel: tierLevel !== -1 ? tierLevel + 1 : 0, // add 1 to make it 1-based
      tiers: applicableRates.length,
    };
  }
  function transformInterestRates(_interestRates: typeof interestRates) {
    return Object.entries(_interestRates).map(([_key, value]) => ({
      ...value,
      Term: value.Term as 'short-term' | 'long-term',
    }));
  }

  function getStatusIcon(iconStatus: string) {
    switch (iconStatus?.toLowerCase()) {
      case 'pending':
      case 'pending approval':
      case 'waiting':
        return (
          <Box pe="sm">
            <Icon color="low-light" icon="pending" size="md" />
          </Box>
        );
      case 'successful':
        return (
          <Box pe="sm">
            <Icon color="low-light" icon="checkGreen" size="md" />
          </Box>
        );
      default:
        return null;
    }
  }

  function transformInterestRateValues(InterestRates: InterestRate[]): (InterestRate & {
    RangeStart: string;
    RangeEnd: string;
  })[] {
    const numberFormat = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return Object.values(InterestRates).map((item) => ({
      ...item,
      AnnualInterestRate: item.AnnualInterestRate,
      Term: item.Term,
      RangeStart: numberFormat.format(parseFloat(item.RangeStart)),
      RangeEnd: numberFormat.format(parseFloat(item.RangeEnd)),
    }));
  }
}
